import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Grid, CircularProgress, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUsersStatisticsByDays,
  selectUsersStatisticsByDays
} from '../../../redux/slices/balanceSlice';
import { selectCities } from '../../../redux/slices/citiesSlice';
import { selectAuth } from '../../../redux/slices/authSlice';
import { selectAdmins } from '../../../redux/slices/adminsSlice';
import { 
  People,
  PersonAdd,
  TrendingUp,
  CardMembership,
  PieChart as PieChartIcon,
  Timeline
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import axios from '../../../resources/axios';
import moment from 'moment';

interface UsersAnalyticsData {
  users: {
    totalUsers: number;
    activeUsers: number;
    activeSubscriptions: number;
    newUsersMonth: number;
    newUsersMonthPercent: number;
    activityDistribution: {
      active: number;
      inactive: number;
    };
    newUsersByDay: Array<{ date: string; count: number }>;
    newSubscriptionsByDay: Array<{ date: string; count: number }>;
  };
}

const UsersStatisticTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  
  const usersStatisticsByDays = useSelector(selectUsersStatisticsByDays);
  const cities = useSelector(selectCities);
  const auth = useSelector(selectAuth);
  const adminInfo = useSelector(selectAdmins);

  const [usersLoading, setUsersLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<UsersAnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const currentCityId = useMemo(() => {
    return adminInfo.currentCity;
  }, [adminInfo.currentCity]);

  useEffect(() => {
    if (!isSuperAdmin && !currentCityId) return;
    
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        await dispatch(fetchUsersStatisticsByDays());
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [dispatch, isSuperAdmin, currentCityId]);

  const handleDateFromChange = (newDateFrom: string) => {
    const daysDiff = moment(dateTo).diff(moment(newDateFrom), 'days');
    if (daysDiff > 60) {
      const adjustedDateTo = moment(newDateFrom).add(60, 'days').format('YYYY-MM-DD');
      const today = moment().format('YYYY-MM-DD');
      setDateTo(adjustedDateTo > today ? today : adjustedDateTo);
    }
    setDateFrom(newDateFrom);
  };

  const handleDateToChange = (newDateTo: string) => {
    setDateTo(newDateTo);
  };

  useEffect(() => {
    if (!isSuperAdmin && !currentCityId) return;
    
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      setError(null);
      try {
        const dateToIso = moment(dateTo).endOf('day').toISOString();
        const dateFromIso = moment(dateFrom).startOf('day').toISOString();
        
        let url = `/analytics/users?dateFrom=${dateFromIso}&dateTo=${dateToIso}`;
        if (!isSuperAdmin && currentCityId) {
          url += `&cityId=${currentCityId}`;
        }
        
        const response = await axios.get(url);
        setAnalyticsData(response.data.data);
      } catch (error) {
        const axiosError = error as any;
        console.error('Error fetching user analytics:', axiosError.response?.status, axiosError.message);
        if (axiosError.response?.status === 400) {
          setError(t('stats.invalidRequestFormat'));
        } else if (axiosError.response?.status === 404) {
          setError(t('stats.endpointNotFound'));
        } else {
          setError(t('stats.loadError'));
        }
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, [isSuperAdmin, currentCityId, dateFrom, dateTo]);

  const hexToRgba = (hex: string, alpha: number = 1): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const StatCard = ({ icon, label, value, color, loading = false, suffix = '' }: { 
    icon: ReactNode, 
    label: string, 
    value: number | string, 
    color: string, 
    loading?: boolean,
    suffix?: string 
  }) => {
    const displayValue = typeof value === 'number' 
      ? value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 })
      : value;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '14px 16px',
          borderRadius: '12px',
          transition: 'all 0.2s ease',
          backgroundColor: hexToRgba(color, 0.12),
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          minWidth: 160,
          minHeight: 80,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              backgroundColor: hexToRgba(color, 0.18),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': {
                fontSize: 24,
                color: color
              }
            }}
          >
            {icon}
          </Box>
          {loading ? (
            <CircularProgress size={32} sx={{ color: color }} />
          ) : (
            <Typography
              sx={{
                fontSize: 32,
                fontWeight: 700,
                color: color,
                lineHeight: 1
              }}
            >
              {displayValue}{suffix}
            </Typography>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            textAlign: 'center'
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const newUsersChartData = useMemo(() => {
    if (!analyticsData?.users.newUsersByDay) return [];
    return analyticsData.users.newUsersByDay.map(item => ({
      date: moment(item.date).format('DD.MM'),
      count: item.count
    }));
  }, [analyticsData]);

  const subscriptionsChartData = useMemo(() => {
    if (!analyticsData?.users.newSubscriptionsByDay) return [];
    return analyticsData.users.newSubscriptionsByDay.map(item => ({
      date: moment(item.date).format('DD.MM'),
      count: item.count
    }));
  }, [analyticsData]);

  const activityPieData = useMemo(() => {
    if (!analyticsData?.users.activityDistribution) return [];
    return [
      { name: t('stats.activeUsers'), value: analyticsData.users.activityDistribution.active, color: '#4CAF50' },
      { name: t('stats.inactiveUsers'), value: analyticsData.users.activityDistribution.inactive, color: '#9E9E9E' }
    ];
  }, [analyticsData]);

  const isLoading = usersLoading || analyticsLoading;

  if (!isSuperAdmin && !currentCityId) {
    return (
      <Paper sx={{ padding: '20px', borderRadius: 2 }}>
        <Typography color="text.secondary">
          {t('stats.selectCityPrompt')}
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ padding: '20px', borderRadius: 2 }}>
        <Typography color="error">
          {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper
        sx={{
          padding: '20px',
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
            }}
          >
            {t('stats.usersStatisticsTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="date"
              label={t('stats.dateFrom')}
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 150 }}
            />
            <TextField
              type="date"
              label={t('stats.dateTo')}
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 150 }}
            />
          </Box>
        </Box>

        <Grid container spacing={2} justifyContent="center" sx={{ mx: 0, width: '100%' }}>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<People />}
              label={t('stats.totalUsers')}
              value={analyticsData?.users.totalUsers ?? 0}
              color='#5073F8'
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<TrendingUp />}
              label={t('stats.activeUsersCount')}
              value={analyticsData?.users.activeUsers ?? 0}
              color='#4CAF50'
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<CardMembership />}
              label={t('stats.activeSubscriptions')}
              value={analyticsData?.users.activeSubscriptions ?? 0}
              color='#9C27B0'
              loading={isLoading}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <StatCard
              icon={<PersonAdd />}
              label={t('stats.newPerMonth')}
              value={analyticsData?.users.newUsersMonth ?? 0}
              color='#FF9800'
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              padding: '20px',
              border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
                mb: 3
              }}
            >
              {t('stats.newUsersPerDayChart')}
            </Typography>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={newUsersChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'} />
                  <XAxis 
                    dataKey="date" 
                    stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#424242',
                      border: 'none',
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                    labelStyle={{ color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#5073F8" 
                    strokeWidth={2}
                    dot={{ fill: '#5073F8', strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: '#5073F8' }}
                    name={t('stats.newUsersName')}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              padding: '20px',
              border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
                mb: 3
              }}
            >
              {t('stats.activityDistribution')}
            </Typography>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={activityPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ value }) => `${value.toFixed(1)}%`}
                  >
                    {activityPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => [`${value.toFixed(1)}%`, '']}
                    contentStyle={{
                      backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#424242',
                      border: 'none',
                      borderRadius: 8
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => (
                      <span style={{ color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF' }}>
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper
        sx={{
          padding: '20px',
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 3
          }}
        >
          {t('stats.newSubscriptionsPerDay')}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={subscriptionsChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'} />
              <XAxis 
                dataKey="date" 
                stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                stroke={theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#424242',
                  border: 'none',
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                labelStyle={{ color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF' }}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#9C27B0" 
                strokeWidth={2}
                dot={{ fill: '#9C27B0', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#9C27B0' }}
                name={t('stats.newSubscriptionsName')}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};

export default UsersStatisticTab;
